import { Priority, PriorityConfig, VisibleToUser, AllReport } from "../types";

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "No due date";

  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // Reset hours for date comparison
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const tomorrowOnly = new Date(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate()
  );

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return `Today, ${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  } else if (dateOnly.getTime() === tomorrowOnly.getTime()) {
    return `Tomorrow, ${date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })}`;
  } else {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  }
};

export const getDaysUntilDue = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "No deadline";

  const dueDate = new Date(dateString);
  const today = new Date();

  // Reset hours for accurate day calculation
  const dueDateOnly = new Date(
    dueDate.getFullYear(),
    dueDate.getMonth(),
    dueDate.getDate()
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const diffTime = dueDateOnly.getTime() - todayOnly.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return `Overdue by ${Math.abs(diffDays)} day(s)`;
  } else if (diffDays === 0) {
    return "Due Today";
  } else if (diffDays === 1) {
    return "Due Tomorrow";
  } else {
    return `${diffDays} days left`;
  }
};

export const getPriorityConfig = (priority: Priority): PriorityConfig => {
  const configs: Record<Priority, PriorityConfig> = {
    high: { color: "danger", icon: "ti-alert-circle" },
    medium: { color: "info", icon: "ti-clock" },
    low: { color: "success", icon: "ti-check-circle" },
  };
  return configs[priority] || configs.medium;
};

// Helper to determine priority based on days until due
export const calculatePriority = (
  dateString: string | null | undefined
): Priority => {
  if (!dateString) return "low";

  const dueDate = new Date(dateString);
  const today = new Date();
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0 || diffDays === 0) {
    return "high"; // Overdue or due today
  } else if (diffDays <= 2) {
    return "high"; // Due within 2 days
  } else if (diffDays <= 5) {
    return "medium"; // Due within 5 days
  } else {
    return "low"; // More than 5 days
  }
};

// Helper to get assigned by name from visible_to array
export const getAssignedByName = (
  visibleToArray: VisibleToUser[] | undefined
): string => {
  if (!visibleToArray || visibleToArray.length === 0) {
    return "Not assigned";
  }

  // Return the first person's name, or you can customize this logic
  const firstAssigned = visibleToArray[0];
  return (
    `${firstAssigned.fname} ${firstAssigned.lname}`.trim() ||
    firstAssigned.username ||
    "Unknown"
  );
};

// Additional helper to format multiple assignees
export const getAllAssigneeNames = (
  visibleToArray: VisibleToUser[] | undefined
): string => {
  if (!visibleToArray || visibleToArray.length === 0) {
    return "Not assigned";
  }

  if (visibleToArray.length === 1) {
    return getAssignedByName(visibleToArray);
  }

  const names = visibleToArray.map(
    (user) => `${user.fname} ${user.lname}`.trim() || user.username
  );

  if (names.length <= 2) {
    return names.join(" and ");
  }

  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} others`;
};

// Helper to check if report is urgent
export const isUrgentReport = (
  dateString: string | null | undefined
): boolean => {
  const priority = calculatePriority(dateString);
  return priority === "high";
};

// Helper to get status badge color based on priority
export const getStatusColor = (
  dateString: string | null | undefined
): string => {
  const priority = calculatePriority(dateString);
  const config = getPriorityConfig(priority);
  return config.color;
};

export const getStatusConfig = (status: AllReport["status"]) => {
  const configs = {
    completed: {
      bgClass: "bg-success-transparent",
      textClass: "text-success",
      icon: "ti-circle-check",
      iconColor: "text-success",
      progressColor: "success",
    },
    pending: {
      bgClass: "bg-warning-transparent",
      textClass: "text-warning",
      icon: "ti-clock",
      iconColor: "text-warning",
      progressColor: "warning",
    },
    draft: {
      bgClass: "bg-info-transparent",
      textClass: "text-info",
      icon: "ti-edit",
      iconColor: "text-info",
      progressColor: "info",
    },
    overdue: {
      bgClass: "bg-danger-transparent",
      textClass: "text-danger",
      icon: "ti-alert-triangle",
      iconColor: "text-danger",
      progressColor: "danger",
    },
  };

  return configs[status] || configs.draft;
};

export const getReportTypeBadgeColor = (type: string): string => {
  const typeColors: Record<string, string> = {
    "Sales Report": "primary",
    "Performance Report": "info",
    "Customer Report": "success",
    "Financial Report": "warning",
  };

  return typeColors[type] || "secondary";
};
